/**
 * Lecture d'un classeur Excel (.xlsx) côté navigateur et conversion de chaque
 * onglet en CSV (délimiteur « ; », le défaut de l'assistant), afin de réutiliser
 * toute la chaîne existante (réconciliation, aperçu, moteur d'import backend)
 * qui ne consomme que des fichiers CSV.
 *
 * Un .xlsx est une archive ZIP de XML : on la décompresse avec fflate (déjà
 * utilisé pour les bundles ZIP) et on parse le XML avec le DOMParser natif.
 * Les cellules formatées « date » (sérials Excel) sont converties en
 * JJ/MM/AAAA, format accepté par le backend (`ImportEngine::normalizeDate`).
 */

import { unzip } from 'fflate';

export interface SheetCsv {
  /** Nom de l'onglet tel qu'affiché dans Excel. */
  name: string;
  /** Contenu CSV (délimiteur « ; », lignes CRLF). */
  csv: string;
}

/** Délimiteur des CSV générés — aligné sur le défaut de la config d'import. */
export const XLSX_CSV_DELIMITER = ';';

function inflate(bytes: Uint8Array): Promise<Record<string, Uint8Array>> {
  return new Promise((resolve, reject) => {
    unzip(bytes, (err, files) => {
      if (err) reject(err);
      else resolve(files);
    });
  });
}

const decoder = new TextDecoder('utf-8');

function parseXml(bytes: Uint8Array | undefined): Document | null {
  if (!bytes) return null;
  const doc = new DOMParser().parseFromString(decoder.decode(bytes), 'application/xml');
  // DOMParser signale les erreurs via un document <parsererror>.
  return doc.getElementsByTagName('parsererror').length > 0 ? null : doc;
}

/** Tous les descendants portant ce localName (namespaces ignorés). */
function byLocalName(root: ParentNode, localName: string): Element[] {
  const out: Element[] = [];
  const walk = (node: ParentNode) => {
    for (const child of Array.from(node.children)) {
      if (child.localName === localName) out.push(child);
      walk(child);
    }
  };
  walk(root);
  return out;
}

/** Texte d'un élément <si> ou <is> : concatène tous ses <t> (texte riche inclus). */
function richText(el: Element): string {
  return byLocalName(el, 't')
    .map((t) => t.textContent ?? '')
    .join('');
}

/** numFmtId intégrés d'Excel correspondant à des dates/heures. */
function isBuiltinDateFormat(id: number): boolean {
  return (id >= 14 && id <= 22) || (id >= 27 && id <= 36) || (id >= 45 && id <= 47) || (id >= 50 && id <= 58);
}

/**
 * Un code de format personnalisé est une date s'il contient des symboles
 * jour/mois/année/heure hors sections entre guillemets, crochets ou échappées.
 */
function isDateFormatCode(code: string): boolean {
  const cleaned = code
    .replace(/"[^"]*"/g, '')
    .replace(/\[[^\]]*\]/g, '')
    .replace(/\\./g, '');
  return /[dmhys]/i.test(cleaned);
}

/** styles.xml → pour chaque index de cellXfs, la cellule est-elle une date. */
function dateStyleFlags(styles: Document | null): boolean[] {
  if (!styles) return [];
  const customDateIds = new Set<number>();
  for (const numFmt of byLocalName(styles.documentElement, 'numFmt')) {
    const id = Number(numFmt.getAttribute('numFmtId'));
    const code = numFmt.getAttribute('formatCode') ?? '';
    if (!Number.isNaN(id) && isDateFormatCode(code)) customDateIds.add(id);
  }
  const cellXfs = byLocalName(styles.documentElement, 'cellXfs')[0];
  if (!cellXfs) return [];
  return byLocalName(cellXfs, 'xf').map((xf) => {
    const id = Number(xf.getAttribute('numFmtId') ?? '0');
    return isBuiltinDateFormat(id) || customDateIds.has(id);
  });
}

/** Sérial Excel → JJ/MM/AAAA (systèmes 1900 et 1904). */
function serialToDate(serial: number, date1904: boolean): string {
  // Système 1900 : sérial 1 = 01/01/1900, epoch effectif 30/12/1899 (le bug
  // historique de l'année 1900 bissextile d'Excel est absorbé par cet epoch
  // pour toute date ≥ 01/03/1900 — les dates réelles d'un import scolaire).
  const base = date1904 ? Date.UTC(1904, 0, 1) : Date.UTC(1899, 11, 30);
  const d = new Date(base + Math.round(serial * 86400000));
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
}

/** Référence de cellule (« C12 ») → index de colonne 0-based. */
function columnIndex(ref: string): number | null {
  const m = /^([A-Z]+)\d*$/i.exec(ref);
  if (!m) return null;
  let idx = 0;
  for (const ch of m[1].toUpperCase()) {
    idx = idx * 26 + (ch.charCodeAt(0) - 64);
  }
  return idx - 1;
}

/** Valeur numérique brute → texte (résout la notation scientifique d'Excel). */
function numberToText(raw: string): string {
  if (/e/i.test(raw)) {
    const n = Number(raw);
    if (Number.isFinite(n)) return String(n);
  }
  return raw;
}

function csvQuote(value: string): string {
  if (
    value.includes(XLSX_CSV_DELIMITER) ||
    value.includes('"') ||
    value.includes('\n') ||
    value.includes('\r')
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

interface WorkbookContext {
  shared: string[];
  isDateStyle: boolean[];
  date1904: boolean;
}

/** Une feuille XML → lignes CSV. */
function sheetToCsv(sheet: Document, ctx: WorkbookContext): string {
  const rows: string[][] = [];
  for (const rowEl of byLocalName(sheet.documentElement, 'row')) {
    const cells: string[] = [];
    let cursor = 0;
    for (const cellEl of byLocalName(rowEl, 'c')) {
      const ref = cellEl.getAttribute('r');
      const col = ref !== null ? columnIndex(ref) : null;
      const target = col ?? cursor;
      while (cells.length < target) cells.push('');
      cells[target] = cellValue(cellEl, ctx);
      cursor = target + 1;
    }
    rows.push(cells);
  }
  // Retire les lignes entièrement vides en fin de feuille (fréquent dans les
  // exports Excel) puis aligne toutes les lignes sur la largeur de l'en-tête.
  while (rows.length > 0 && rows[rows.length - 1].every((c) => c.trim() === '')) {
    rows.pop();
  }
  const width = rows[0]?.length ?? 0;
  return rows
    .map((cells) => {
      const padded = cells.slice(0, Math.max(width, cells.length));
      while (padded.length < width) padded.push('');
      return padded.map(csvQuote).join(XLSX_CSV_DELIMITER);
    })
    .join('\r\n');
}

function cellValue(cellEl: Element, ctx: WorkbookContext): string {
  const type = cellEl.getAttribute('t') ?? 'n';
  if (type === 'inlineStr') {
    const is = byLocalName(cellEl, 'is')[0];
    return is ? richText(is) : '';
  }
  const v = byLocalName(cellEl, 'v')[0]?.textContent ?? '';
  switch (type) {
    case 's': {
      const idx = Number(v);
      return ctx.shared[idx] ?? '';
    }
    case 'str':
      return v;
    case 'b':
      return v === '1' ? '1' : '0';
    case 'e':
      return '';
    default: {
      // Numérique : une cellule au style « date » porte un sérial Excel.
      if (v === '') return '';
      const styleIdx = Number(cellEl.getAttribute('s') ?? '-1');
      if (ctx.isDateStyle[styleIdx]) {
        const serial = Number(v);
        if (Number.isFinite(serial)) return serialToDate(serial, ctx.date1904);
      }
      return numberToText(v);
    }
  }
}

/**
 * Lit un fichier .xlsx et retourne un CSV par onglet, dans l'ordre du classeur.
 * Rejette (Error avec message FR) si le fichier n'est pas un classeur lisible.
 */
export async function workbookToCsvSheets(file: File): Promise<SheetCsv[]> {
  let entries: Record<string, Uint8Array>;
  try {
    entries = await inflate(new Uint8Array(await file.arrayBuffer()));
  } catch {
    throw new Error('Classeur illisible : le fichier ne semble pas être un .xlsx valide.');
  }

  const workbook = parseXml(entries['xl/workbook.xml']);
  if (!workbook) {
    throw new Error('Classeur illisible : structure xl/workbook.xml absente ou invalide.');
  }
  const rels = parseXml(entries['xl/_rels/workbook.xml.rels']);

  // rId → chemin de la feuille dans l'archive (relatif à xl/).
  const relTargets = new Map<string, string>();
  if (rels) {
    for (const rel of byLocalName(rels.documentElement, 'Relationship')) {
      const id = rel.getAttribute('Id');
      let target = rel.getAttribute('Target') ?? '';
      if (target.startsWith('/')) target = target.slice(1);
      else target = `xl/${target}`;
      if (id) relTargets.set(id, target.replace(/\/\.\//g, '/'));
    }
  }

  const date1904 =
    byLocalName(workbook.documentElement, 'workbookPr')[0]?.getAttribute('date1904') === '1';

  const shared = (() => {
    const doc = parseXml(entries['xl/sharedStrings.xml']);
    if (!doc) return [];
    return byLocalName(doc.documentElement, 'si').map(richText);
  })();

  const ctx: WorkbookContext = {
    shared,
    isDateStyle: dateStyleFlags(parseXml(entries['xl/styles.xml'])),
    date1904,
  };

  const sheets: SheetCsv[] = [];
  const sheetEls = byLocalName(workbook.documentElement, 'sheet');
  for (const [i, sheetEl] of sheetEls.entries()) {
    const name = sheetEl.getAttribute('name') ?? `Feuille${i + 1}`;
    const rid =
      sheetEl.getAttribute('r:id') ??
      sheetEl.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id');
    const path = (rid !== null ? relTargets.get(rid) : undefined) ?? `xl/worksheets/sheet${i + 1}.xml`;
    const doc = parseXml(entries[path]);
    if (!doc) continue;
    sheets.push({ name, csv: sheetToCsv(doc, ctx) });
  }

  if (sheets.length === 0) {
    throw new Error('Classeur vide : aucun onglet lisible.');
  }
  return sheets;
}
